<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';

const props = defineProps({
    text: { type: String, default: '' },
    messageType: { type: String, default: 'reply' },
    mentionableUsers: { type: Array, default: () => [] },
    composer: { type: Object, default: () => ({}) },
    labels: { type: Object, default: () => ({}) },
    ticketLabels: { type: Object, default: () => ({}) },
    canAddMessage: { type: Boolean, default: false },
    assistBusy: { type: Boolean, default: false },
    /** Matches ticketing.attachments.max_size_mb default. */
    maxAttachmentMb: { type: Number, default: 25 },
    /** Closed/merged tickets allow internal notes only — no customer reply mode. */
    ticketOpen: { type: Boolean, default: true },
});

const emit = defineEmits([
    'update:text', 'update:messageType', 'send-message',
    'discard-draft', 'clear-draft-ui', 'toast-error',
]);

const fileInputEl = ref(null);
const textareaEl = ref(null);
const selectedFiles = ref([]);
const mentionOpen = ref(false);
const mentionIndex = ref(0);
const mentionQuery = ref('');
const mentionStart = ref(-1);

const REPLY_MIN_HEIGHT_PX = 120;

const fitReplyHeight = () => {
    const el = textareaEl.value;
    if (!el) {
        return;
    }
    el.style.height = 'auto';
    const next = Math.max(REPLY_MIN_HEIGHT_PX, el.scrollHeight);
    el.style.height = `${next}px`;
};

/** Category confidence band (ops-only). Thresholds align with auto_apply_category_min_confidence (0.85). */
const categoryConfidenceBand = computed(() => {
    const raw = props.composer?.pending_ai_draft?.confidence;
    if (raw === null || raw === undefined || raw === '') {
        return null;
    }
    const value = Number(raw);
    if (Number.isNaN(value)) {
        return null;
    }
    if (value >= 0.85) {
        return 'Alta';
    }
    if (value >= 0.5) {
        return 'Media';
    }

    return 'Bassa';
});

const allowCustomerReply = computed(() => props.ticketOpen);
const isReply = computed(() => allowCustomerReply.value && props.messageType !== 'note');
const hasComposerText = computed(() => Boolean(props.text.trim()));
const canSend = computed(() => props.canAddMessage && hasComposerText.value && !props.assistBusy);
const maxAttachmentBytes = computed(() => Math.max(1, props.maxAttachmentMb) * 1024 * 1024);
const showDraftActions = computed(() => Boolean(props.composer?.can_discard || props.composer?.can_clear_ui));
const showComposerFooter = computed(() => props.messageType === 'note' || showDraftActions.value);
const showMessageTypeToggle = computed(() => allowCustomerReply.value);

const messageTypeFieldLabel = computed(() => (
    props.labels.messageType
    || props.labels.message_type
    || 'Tipo messaggio'
));

const customerResponseOptionLabel = computed(() => (
    props.labels.customer_response
    || props.labels.customerResponse
    || 'Risposta al cliente'
));

const internalNoteOptionLabel = computed(() => (
    props.labels.internal_note_type
    || props.labels.internalNote
    || 'Nota interna'
));

const onMessageTypeSelect = (event) => {
    const value = event.target.value;
    setMessageType(value === 'note' ? 'note' : 'reply');
};

const fileKey = (file) => [file.name, file.size, file.lastModified].join('-');

const clearSelectedFiles = () => {
    selectedFiles.value = [];
    if (fileInputEl.value) {
        fileInputEl.value.value = '';
    }
};

const setMessageType = (type) => {
    if (!allowCustomerReply.value && type === 'reply') {
        return;
    }
    if (type === 'note') {
        clearSelectedFiles();
    }
    emit('update:messageType', type);
};

watch(
    () => props.ticketOpen,
    (open) => {
        if (!open && props.messageType !== 'note') {
            clearSelectedFiles();
            emit('update:messageType', 'note');
        }
    },
    { immediate: true },
);

const openFilePicker = () => {
    if (!isReply.value || props.assistBusy) {
        return;
    }
    fileInputEl.value?.click();
};

const onFilesPicked = (event) => {
    const picked = Array.from(event.target.files || []);
    const maxBytes = maxAttachmentBytes.value;
    const next = selectedFiles.value.slice();
    let rejected = false;

    picked.forEach((file) => {
        if (file.size > maxBytes) {
            rejected = true;
            return;
        }
        const key = fileKey(file);
        if (!next.some((existing) => fileKey(existing) === key)) {
            next.push(file);
        }
    });

    selectedFiles.value = next;
    if (fileInputEl.value) {
        fileInputEl.value.value = '';
    }
    if (rejected) {
        emit('toast-error', props.labels.attachmentTooLarge
            || `Allegato troppo grande (max ${props.maxAttachmentMb} MB)`);
    }
};

const removeFile = (key) => {
    selectedFiles.value = selectedFiles.value.filter((file) => fileKey(file) !== key);
};

const mentionCandidates = computed(() => {
    const query = mentionQuery.value.toLowerCase();
    return (props.mentionableUsers || []).filter((user) => {
        const haystack = `${user.mention || ''} ${user.label || ''}`.toLowerCase();
        return query === '' || haystack.includes(query);
    });
});

const mentionedIdsFromText = (text) => {
    const haystack = text.toLowerCase();

    return (props.mentionableUsers || [])
        .filter((user) => user.mention && haystack.includes(`@${String(user.mention).toLowerCase()}`))
        .map((user) => user.id);
};

const closeMentionMenu = () => {
    mentionOpen.value = false;
    mentionQuery.value = '';
    mentionStart.value = -1;
};

const pickMention = (user) => {
    if (!user?.mention) {
        return;
    }
    const token = `@${user.mention} `;
    const start = mentionStart.value < 0 ? props.text.length : mentionStart.value;
    const cursor = textareaEl.value?.selectionStart ?? props.text.length;
    const next = `${props.text.slice(0, start)}${token}${props.text.slice(cursor)}`;
    emit('update:text', next);
    closeMentionMenu();
    requestAnimationFrame(() => {
        const position = start + token.length;
        textareaEl.value?.focus();
        textareaEl.value?.setSelectionRange(position, position);
    });
};

const sendMessage = () => {
    if (!canSend.value) {
        return;
    }
    emit('send-message', {
        type: props.messageType,
        text: props.text,
        files: isReply.value ? selectedFiles.value.slice() : [],
        mentionedUserIds: props.messageType === 'note' ? mentionedIdsFromText(props.text) : [],
    });
    clearSelectedFiles();
    closeMentionMenu();
};

watch(() => props.text, (value) => {
    if (!value) {
        clearSelectedFiles();
    }
    nextTick(() => fitReplyHeight());
});

watch(
    () => props.composer?.ai_draft,
    () => {
        nextTick(() => fitReplyHeight());
    },
);

onMounted(() => {
    nextTick(() => fitReplyHeight());
});

const mentionQueryHasTrailingText = (typed, users) => {
    const typedLower = String(typed).toLowerCase();
    return (users || []).some((user) => {
        const token = String(user.mention || '').trim().toLowerCase();
        if (token === '') {
            return false;
        }
        const prefix = `${token} `;
        return typedLower.startsWith(prefix) && typedLower.slice(prefix.length) !== '';
    });
};

const onReplyInput = (event) => {
    const value = event.target.value;
    emit('update:text', value);
    fitReplyHeight();
    const cursor = event.target.selectionStart ?? value.length;
    const before = value.slice(0, cursor);
    const match = before.match(/(^|[\s])@([^\n@]*)$/);
    if (!match) {
        closeMentionMenu();
        return;
    }
    if (props.messageType !== 'note') {
        setMessageType('note');
    }
    if (mentionQueryHasTrailingText(match[2], props.mentionableUsers)) {
        closeMentionMenu();
        return;
    }
    mentionQuery.value = match[2];
    mentionStart.value = before.length - match[2].length - 1;
    mentionIndex.value = 0;
    mentionOpen.value = true;
};

const onMentionKeydown = (event) => {
    if (!mentionOpen.value) {
        return;
    }
    if (event.key === 'ArrowDown') {
        event.preventDefault();
        mentionIndex.value = (mentionIndex.value + 1) % Math.max(mentionCandidates.value.length, 1);
        return;
    }
    if (event.key === 'ArrowUp') {
        event.preventDefault();
        const length = Math.max(mentionCandidates.value.length, 1);
        mentionIndex.value = (mentionIndex.value - 1 + length) % length;
        return;
    }
    if (event.key === 'Enter' && mentionCandidates.value[mentionIndex.value]) {
        event.preventDefault();
        pickMention(mentionCandidates.value[mentionIndex.value]);
        return;
    }
    if (event.key === 'Escape') {
        event.preventDefault();
        closeMentionMenu();
    }
};

watch(
    () => props.messageType,
    (type) => {
        if (type !== 'note') {
            closeMentionMenu();
        }
    },
);

watch(mentionCandidates, (candidates) => {
    if (!mentionOpen.value) {
        return;
    }
    if (mentionIndex.value >= candidates.length) {
        mentionIndex.value = 0;
    }
});
</script>

<template>
    <section class="ticket-desk-composer" data-testid="ticket-desk-composer">
        <div v-if="canAddMessage" class="ticket-desk-composer__main">
            <div v-if="composer.ai_draft" class="ticket-desk-ai-banner">
                <span aria-hidden="true">✦</span>
                <strong>{{ labels.aiDraftReady || labels.ai_draft_suggestion || 'Bozza AI pronta' }}</strong>
                <span>{{ labels.aiDraftWarning || '· Verifica sempre dettagli e tono prima dell’invio.' }}</span>
                <span
                    v-if="categoryConfidenceBand"
                    data-testid="ticket-desk-category-confidence"
                >
                    · Confidenza categoria: {{ categoryConfidenceBand }}
                </span>
            </div>

            <div class="ticket-desk-composer__shell">
                <div class="ticket-desk-composer__input">
                    <textarea
                        id="ticket-reply-body"
                        ref="textareaEl"
                        class="ticket-desk-composer__reply"
                        :class="{ 'ticket-desk-composer__reply--ai-prefilled': composer.ai_draft }"
                        data-testid="ticket-desk-composer-reply-wrap"
                        :value="text"
                        :placeholder="messageType === 'reply'
                            ? (ticketLabels.reply || 'Scrivi una risposta al cliente…')
                            : (ticketLabels.internalNote || 'Aggiungi una nota interna…')"
                        :disabled="!canAddMessage || assistBusy"
                        @input="onReplyInput"
                        @keydown="onMentionKeydown"
                    />
                    <ul
                        v-if="mentionOpen"
                        class="ticket-desk-mention-menu"
                        data-testid="ticket-desk-mention-menu"
                        role="listbox"
                    >
                        <li v-if="!mentionCandidates.length">
                            <span class="ticket-desk-mention-menu__empty">
                                {{ labels.mentionNone || labels.mention_none || 'Nessun collega taggabile su questo canale' }}
                            </span>
                        </li>
                        <li v-for="(user, index) in mentionCandidates" :key="user.id">
                            <button
                                type="button"
                                class="ticket-desk-mention-menu__item"
                                :class="{ 'is-active': index === mentionIndex }"
                                role="option"
                                :aria-selected="index === mentionIndex"
                                @mousedown.prevent="pickMention(user)"
                            >
                                {{ user.label }}
                            </button>
                        </li>
                    </ul>
                    <ul
                        v-if="selectedFiles.length"
                        class="ticket-desk-composer__attach-preview"
                        data-testid="ticket-desk-attach-preview"
                    >
                        <li
                            v-for="file in selectedFiles"
                            :key="fileKey(file)"
                            class="ticket-desk-composer__attach-chip"
                        >
                            <span class="ticket-desk-composer__attach-name">{{ file.name }}</span>
                            <button
                                type="button"
                                class="ticket-desk-composer__attach-remove"
                                :aria-label="labels.removeAttachment || 'Rimuovi allegato'"
                                @click="removeFile(fileKey(file))"
                            >
                                ×
                            </button>
                        </li>
                    </ul>
                    <div
                        v-if="showComposerFooter"
                        class="ticket-desk-composer__hint"
                        data-testid="ticket-desk-composer-hint"
                    >
                        <span
                            v-if="messageType === 'note'"
                            class="ticket-desk-composer__hint-note"
                            aria-live="polite"
                        >
                            {{ labels.noteHint || labels.note_hint || 'Apparirà in Note interne · solo operatori' }}
                        </span>
                        <div
                            v-if="showDraftActions"
                            class="ticket-desk-composer__hint-actions"
                        >
                            <button
                                v-if="composer.can_discard"
                                type="button"
                                class="ticket-desk-button ticket-desk-button--ghost ticket-desk-button--sm"
                                data-testid="ticket-desk-discard-draft"
                                :disabled="assistBusy"
                                @click="emit('discard-draft')"
                            >
                                {{ labels.discardDraft || labels.discard_draft || 'Rifiuta bozza' }}
                            </button>
                            <button
                                v-if="composer.can_clear_ui"
                                type="button"
                                class="ticket-desk-button ticket-desk-button--ghost ticket-desk-button--sm"
                                data-testid="ticket-desk-hide-draft"
                                :disabled="assistBusy"
                                @click="emit('clear-draft-ui')"
                            >
                                {{ labels.clearDraftUi || labels.clear_draft || 'Nascondi bozza' }}
                            </button>
                        </div>
                    </div>
                </div>

                <div class="ticket-desk-composer__controls">
                    <div
                        v-if="showMessageTypeToggle"
                        class="ticket-desk-composer__mode"
                        data-testid="ticket-desk-composer-mode"
                    >
                        <label
                            class="ticket-desk-composer__type-label ticket-desk-composer__type-label--sr"
                            for="ticket-desk-message-type"
                        >
                            {{ messageTypeFieldLabel }}
                        </label>
                        <div class="ticket-desk-composer__type-fit">
                            <div
                                class="ticket-desk-composer__type-sizer"
                                aria-hidden="true"
                            >
                                <span>{{ customerResponseOptionLabel }}</span>
                                <span>{{ internalNoteOptionLabel }}</span>
                            </div>
                            <select
                                id="ticket-desk-message-type"
                                class="ticket-desk-composer__type-select"
                                :class="{ 'ticket-desk-composer__type-select--reply': messageType === 'reply' }"
                                data-testid="ticket-desk-composer-message-type"
                                :value="messageType"
                                :aria-label="messageTypeFieldLabel"
                                :disabled="!canAddMessage || assistBusy"
                                @change="onMessageTypeSelect"
                            >
                                <option
                                    value="reply"
                                    data-testid="ticket-desk-composer-mode-reply"
                                >
                                    {{ customerResponseOptionLabel }}
                                </option>
                                <option
                                    value="note"
                                    data-testid="ticket-desk-composer-mode-note"
                                >
                                    {{ internalNoteOptionLabel }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <div
                        v-else
                        class="ticket-desk-composer__mode ticket-desk-composer__mode--note-only"
                        data-testid="ticket-desk-composer-mode-note-only"
                        :title="internalNoteOptionLabel"
                    >
                        <div class="ticket-desk-composer__type-fit">
                            <div
                                class="ticket-desk-composer__type-sizer"
                                aria-hidden="true"
                            >
                                <span>{{ internalNoteOptionLabel }}</span>
                            </div>
                            <span class="ticket-desk-composer__type-static">
                                {{ internalNoteOptionLabel }}
                            </span>
                        </div>
                    </div>

                    <div class="ticket-desk-composer__send-row">
                        <input
                            ref="fileInputEl"
                            type="file"
                            class="ticket-desk-composer__file-input"
                            multiple
                            data-testid="ticket-desk-attach-input"
                            @change="onFilesPicked"
                        >
                        <button
                            type="button"
                            class="ticket-desk-composer__attach"
                            :class="{ 'is-invisible': !isReply }"
                            data-testid="ticket-desk-attach"
                            :disabled="assistBusy || !isReply"
                            :tabindex="isReply ? 0 : -1"
                            :aria-hidden="!isReply"
                            :aria-label="labels.attachFile || 'Allega file'"
                            :title="labels.attachFile || 'Allega file'"
                            @click="openFilePicker"
                        >
                            <span class="ticket-desk-icon ticket-desk-composer__attach-icon" aria-hidden="true">
                                <svg viewBox="0 0 24 24" focusable="false">
                                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                                </svg>
                            </span>
                        </button>
                        <button
                            type="button"
                            class="ticket-desk-composer__send"
                            :class="isReply ? 'ticket-desk-composer__send--reply' : 'ticket-desk-composer__send--note'"
                            data-testid="ticket-desk-send"
                            :disabled="!canSend"
                            :aria-label="labels.send || 'Invia'"
                            :title="labels.send || 'Invia'"
                            @click="sendMessage"
                        >
                            <span class="ticket-desk-icon ticket-desk-composer__send-icon" aria-hidden="true">
                                <svg viewBox="0 0 24 24" focusable="false">
                                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                                </svg>
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
